import { useRequest } from 'features/request/useRequest';
import type { BimChapterEnum, BimPriceLevelEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { bimWorkOrderVersionPricesGql } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/bimWorkOrderPriceQueries';
import type { ExtendedBimWorkOrderPriceVersionData } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/hooks/helpers';
import {
    getWhereVariables,
    prepareCodeToSort,
} from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/hooks/helpers';

import { useEffect, useMemo } from 'react';

export const useBimWorkOrderVersionPrice = ({
    workOrderVersionId,
    workOrderVersionRootObjectId,
    chapter,
    includeRows = false,
    filterValues,
    priceLevel,
}: {
    chapter: BimChapterEnum | undefined;
    workOrderVersionId: number;
    workOrderVersionRootObjectId?: number;
    includeRows?: boolean;
    filterValues?: Record<string, string[]>;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const getPriceListDataVariables = useMemo(() => {
        if (chapter && workOrderVersionId) {
            return {
                workOrderVersionId,
                workOrderVersionRootObjectId,
                chapter,
                includeRows,
                priceLevel,
                where: getWhereVariables(filterValues),
            };
        }

        return undefined;
    }, [chapter, filterValues, includeRows, priceLevel, workOrderVersionId, workOrderVersionRootObjectId]);

    const [bimWorkOrderPriceListData, , , bimWorkOrderPriceListRequest] = useRequest({
        requestId: 'bimWorkOrderPriceVersionList',
        query: bimWorkOrderVersionPricesGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'bimWorkOrderPriceVersionList',
        requestFn: bimWorkOrderPriceListRequest,
        requestVariables: getPriceListDataVariables,
    });

    useEffect(() => {
        const dataWithCodeNumber = bimWorkOrderPriceListData?.bimWorkOrderVersionPrices?.items?.map((item) => ({
            ...item,
            code: prepareCodeToSort(item.completedValue.code ?? ''),
        }));

        const sortedDataWithCodeNumber = dataWithCodeNumber?.sort(
            (prev: ExtendedBimWorkOrderPriceVersionData, next: ExtendedBimWorkOrderPriceVersionData) =>
                prev.code - next.code,
        );

        const sortedDataLsr = sortedDataWithCodeNumber?.map((item) => {
            const { code, ...rest } = item;

            return rest;
        });

        if (sortedDataLsr && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                sortedDataLsr,
                bimWorkOrderPriceListData.bimWorkOrderVersionPrices?.totalCount,
            );
        }
    }, [
        getRowParams,
        bimWorkOrderPriceListData?.bimWorkOrderVersionPrices?.totalCount,
        bimWorkOrderPriceListData?.bimWorkOrderVersionPrices?.items,
    ]);

    const priceListCounter = useMemo(
        () => bimWorkOrderPriceListData?.bimWorkOrderVersionPrices?.totalCount ?? 0,
        [bimWorkOrderPriceListData?.bimWorkOrderVersionPrices?.totalCount],
    );

    const fetchedItemsCounter = useMemo(() => {
        if (!getRowParams?.current?.endRow || !priceListCounter) {
            return 0;
        }

        if (getRowParams?.current?.endRow > priceListCounter) {
            return priceListCounter;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [priceListCounter, getRowParams?.current?.endRow]);

    return {
        bimPriceListDataSource: dataSource,
        priceListCounter,
        fetchedItemsCounter,
    };
};
